// ============================================
// MODALESCANEARCOTIZACION.JS — Sube un PDF de cotizacion y muestra los detectados
// ============================================
// FASE 2: sube el PDF al endpoint POST /api/cotizaciones/escanear (con auth),
// que extrae las lineas (sin IA) y las cruza con el catalogo. De momento SOLO
// muestra el resultado (confirmar + armar la cotizacion van en el siguiente paso).

import React, { useEffect, useRef, useState } from 'react';
import ExpedienteTypeahead from './ExpedienteTypeahead';
import {
  API_BASE_CLI, PP, COLOR_OSCURO, COLOR_GRIS, COLOR_GRIS_CLARO, COLOR_BORDE,
} from './helpersClientes';

function authHeaders() {
  const t = localStorage.getItem('beep_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

// Color por grupo: verde encontrado, ambar por elegir, gris servicio, rojo no encontrado.
const COLORES = {
  seguro:   { bg: '#e8faf5', borde: '#b8e8d4', texto: '#16a34a' },
  dudoso:   { bg: '#fff8ed', borde: '#fce5b8', texto: '#a86b1e' },
  servicio: { bg: '#f3f4f6', borde: '#e5e7eb', texto: '#6b7280' },
  error:    { bg: '#fde8e8', borde: '#f5b8b8', texto: '#dc2626' },
};
// Grupo segun el ESTADO ACTUAL (editable) de la linea.
function grupoLinea(l) {
  if (l.excluida) return 'excluida';
  if (l.esServicio) return 'servicio';
  if (l.producto) return 'seguro';
  if (l.candidatos && l.candidatos.length > 0) return 'dudoso'; // por elegir
  return 'error'; // no encontrado / pendiente
}
function etiquetaGrupo(g) {
  return ({ seguro: 'Producto', dudoso: 'Por elegir', servicio: 'Servicio', error: 'No encontrado', excluida: 'Excluida' })[g] || g;
}
// Estado inicial editable de una linea, a partir del resultado del escaneo.
function lineaInicial(r) {
  const seguro = ['exacto', 'normalizado', 'equivalencia', 'prefijo'].includes(r.estado);
  return {
    clave: r.clave,
    descripcion: r.descripcion || null,
    estadoOriginal: r.estado,
    cantidad: r.cantidad != null && r.cantidad > 0 ? r.cantidad : 1,
    producto: seguro ? (r.candidatos && r.candidatos[0]) || null : null,
    candidatos: r.candidatos || [],
    esServicio: r.estado === 'no_catalogo',
    precioUnitario: r.precio_unitario != null ? Number(r.precio_unitario) : null, // del PDF (para servicios)
    excluida: false,
    buscando: false,
  };
}
// Detalle tecnico (al hover del badge): el "como" del match.
function detalleTecnico(estado) {
  return ({
    exacto: 'Coincidencia exacta de modelo',
    normalizado: 'Coincide ignorando guiones y mayusculas',
    prefijo: 'Coincide quitando el sufijo del modelo',
    equivalencia: 'Aprendido de una confirmacion previa',
    ambiguo: 'Hay varias opciones, tu eliges cual',
    no_catalogo: 'Mano de obra o concepto sin producto',
    sin_match: 'No aparece en el catalogo',
  })[estado] || estado;
}

export default function ModalEscanearCotizacion({ abierto, onCerrar, onCreada }) {
  const inputRef = useRef(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [nombreArchivo, setNombreArchivo] = useState(null);

  // Hooks SIEMPRE antes de cualquier return.
  if (!abierto) return null;

  const subir = async (file) => {
    if (!file) return;
    setNombreArchivo(file.name);
    setError(null);
    setData(null);
    setCargando(true);
    try {
      const fd = new FormData();
      fd.append('pdf', file);
      const res = await fetch(`${API_BASE_CLI}/api/cotizaciones/escanear`, {
        method: 'POST',
        headers: { ...authHeaders() },
        body: fd,
      });
      const r = await res.json().catch(() => ({}));
      if (res.status === 401 || res.status === 403) {
        setError('Tu sesion expiro o no tienes permiso. Vuelve a iniciar sesion.');
      } else if (!res.ok || !r.ok) {
        setError(r.error || 'No se pudo procesar el PDF');
      } else {
        setData(r);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setCargando(false);
    }
  };

  const reiniciar = () => { setData(null); setNombreArchivo(null); setError(null); };

  return (
    <div
      onClick={onCerrar}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1500,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 14, width: 'min(100%, 840px)', maxHeight: '90vh',
          display: 'flex', flexDirection: 'column', fontFamily: PP, color: COLOR_GRIS,
          boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
        }}
      >
        <div style={{
          padding: '16px 20px', borderBottom: `1px solid ${COLOR_BORDE}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: COLOR_OSCURO }}>📄 Escanear cotizacion (PDF)</div>
          <button onClick={onCerrar} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: COLOR_GRIS_CLARO, lineHeight: 1 }}>×</button>
        </div>

        <div style={{ padding: 20, overflowY: 'auto' }}>
          {!data && (
            <div style={{ textAlign: 'center', padding: '10px 0 16px' }}>
              <input
                ref={inputRef}
                type="file"
                accept="application/pdf,.pdf"
                style={{ display: 'none' }}
                onChange={(e) => subir(e.target.files && e.target.files[0])}
              />
              <button
                onClick={() => inputRef.current && inputRef.current.click()}
                disabled={cargando}
                style={{
                  padding: '12px 20px', borderRadius: 10, border: 'none',
                  background: '#185FA5', color: '#fff', fontWeight: 700, fontSize: 14,
                  cursor: cargando ? 'not-allowed' : 'pointer', fontFamily: PP, opacity: cargando ? 0.6 : 1,
                }}
              >{cargando ? '⏳ Leyendo PDF…' : '📎 Elegir PDF de cotizacion'}</button>
              {nombreArchivo && <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginTop: 10 }}>{nombreArchivo}</div>}
              <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginTop: 14, lineHeight: 1.5, maxWidth: 460, margin: '14px auto 0' }}>
                Sube el PDF de cotizacion del socio. Detectamos las lineas y las cruzamos con el catalogo.
                Por ahora solo se muestra el resultado (confirmar y armar la cotizacion van en el siguiente paso).
              </div>
            </div>
          )}

          {error && (
            <div style={{ background: '#fde8e8', border: '1px solid #f5b8b8', color: '#dc2626', borderRadius: 8, padding: 12, fontSize: 13, marginTop: 8 }}>
              {error}
            </div>
          )}

          {data && <Resultados data={data} onOtro={reiniciar} onCreada={onCreada} />}
        </div>
      </div>
    </div>
  );
}

function Resultados({ data, onOtro, onCreada }) {
  const { lineas_detectadas = 0, resultados = [] } = data;
  // Estado editable: una entrada por linea. Se reinicia al escanear otro PDF
  // porque el componente se remonta cuando cambia 'data'.
  const [lineas, setLineas] = useState(() => resultados.map(lineaInicial));
  const [oportunidad, setOportunidad] = useState(null);
  const [creando, setCreando] = useState(false);
  const [errorCrear, setErrorCrear] = useState(null);
  const patch = (i, p) => setLineas(prev => prev.map((l, k) => (k === i ? { ...l, ...p } : l)));

  // Contadores en vivo de la seleccion.
  const productosListos = lineas.filter(l => !l.excluida && !l.esServicio && l.producto).length;
  const serviciosListos = lineas.filter(l => !l.excluida && l.esServicio).length;
  const pendientes = lineas.filter(l => !l.excluida && !l.esServicio && !l.producto).length;
  const listos = productosListos + serviciosListos;

  const crear = async () => {
    const sel = lineas.filter(l => !l.excluida);
    const body = {
      oportunidad_id: oportunidad ? oportunidad.id : null,
      lineas: sel.map(l => (l.esServicio
        ? { clave: l.clave, cantidad: l.cantidad, servicio: true, descripcion: l.descripcion, precio_unitario: l.precioUnitario || 0 }
        // fuente: la del candidato elegido (syscom/beep). Default syscom (compat).
        : { clave: l.clave, cantidad: l.cantidad, producto_id: l.producto.producto_id, fuente: l.producto.fuente || 'syscom' })),
    };
    setCreando(true);
    setErrorCrear(null);
    try {
      const res = await fetch(`${API_BASE_CLI}/api/cotizaciones/desde-escaneo`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() }, body: JSON.stringify(body),
      });
      const r = await res.json().catch(() => ({}));
      if (res.status === 401 || res.status === 403) setErrorCrear('Tu sesion expiro o no tienes permiso. Vuelve a iniciar sesion.');
      else if (!res.ok || !r.ok) setErrorCrear(r.error || 'No se pudo crear la cotizacion');
      else if (onCreada) onCreada(r.cotizacion_id);
    } catch (e) {
      setErrorCrear(e.message);
    } finally {
      setCreando(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 14, color: COLOR_OSCURO, fontWeight: 700 }}>
          {lineas_detectadas} linea{lineas_detectadas === 1 ? '' : 's'} detectada{lineas_detectadas === 1 ? '' : 's'}
        </div>
        <button onClick={onOtro} style={{ fontSize: 12, color: '#185FA5', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, fontFamily: PP }}>↻ Escanear otro</button>
      </div>

      {/* Contador de seleccion lista */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: COLOR_OSCURO }}>
          {productosListos} producto{productosListos === 1 ? '' : 's'} + {serviciosListos} servicio{serviciosListos === 1 ? '' : 's'} listos
        </span>
        {pendientes > 0 && (
          <span title="Lineas sin producto asignado ni marcadas como servicio"
            style={{ fontSize: 12, padding: '3px 9px', borderRadius: 99, cursor: 'help', background: COLORES.error.bg, border: `1px solid ${COLORES.error.borde}`, color: COLORES.error.texto, fontWeight: 600 }}>
            {pendientes} pendiente{pendientes === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {lineas.length === 0 ? (
        <div style={{ padding: 20, textAlign: 'center', color: COLOR_GRIS_CLARO, fontSize: 13 }}>
          No se detectaron lineas de producto. El formato del PDF puede diferir del esperado;
          comparte el archivo para afinar el lector.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {lineas.map((l, i) => <FilaLinea key={i} l={l} onPatch={(p) => patch(i, p)} />)}
        </div>
      )}

      {lineas.length > 0 && (
        <div style={{ marginTop: 14, borderTop: `1px solid ${COLOR_BORDE}`, paddingTop: 12 }}>
          <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 6 }}>
            Vincular a expediente (opcional)
          </div>
          <ExpedienteTypeahead valor={oportunidad} onElegir={setOportunidad} />

          {errorCrear && (
            <div style={{ color: '#dc2626', fontSize: 12, marginTop: 8 }}>{errorCrear}</div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, gap: 10, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 12, color: COLOR_GRIS }}>
              {pendientes > 0
                ? `Resuelve las ${pendientes} línea${pendientes === 1 ? '' : 's'} pendiente${pendientes === 1 ? '' : 's'} para crear.`
                : `Se creará con ${productosListos} producto${productosListos === 1 ? '' : 's'} + ${serviciosListos} servicio${serviciosListos === 1 ? '' : 's'}.`}
            </div>
            <button
              onClick={crear}
              disabled={creando || pendientes > 0 || listos === 0}
              style={{
                padding: '10px 18px', borderRadius: 10, border: 'none', color: '#fff',
                background: (creando || pendientes > 0 || listos === 0) ? '#9bbcd8' : '#16a34a',
                fontWeight: 700, fontSize: 14, fontFamily: PP,
                cursor: (creando || pendientes > 0 || listos === 0) ? 'not-allowed' : 'pointer',
              }}
            >{creando ? '⏳ Creando…' : '✓ Crear cotización'}</button>
          </div>
        </div>
      )}
    </div>
  );
}

// El selector de expediente ahora es el componente compartido ExpedienteTypeahead
// (mismo patron server-side). Se elimino la copia interna para no duplicar.

// Una linea revisable: cantidad, producto/servicio/excluir, buscador inline.
function FilaLinea({ l, onPatch }) {
  const g = grupoLinea(l);
  const col = COLORES[g] || COLORES.servicio;
  const prod = l.producto;

  return (
    <div style={{
      border: `1px solid ${COLOR_BORDE}`, borderRadius: 10, padding: 10,
      opacity: l.excluida ? 0.55 : 1, background: l.excluida ? '#fafafa' : '#fff',
    }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <Miniatura src={prod && prod.img_portada} grupo={g} />

        <div style={{ flexShrink: 0 }}>
          <div style={{ fontSize: 10, color: COLOR_GRIS_CLARO, textTransform: 'uppercase', marginBottom: 2 }}>Cant</div>
          <input
            type="number" min="1" value={l.cantidad} disabled={l.excluida}
            onChange={(e) => onPatch({ cantidad: Math.max(1, parseInt(e.target.value, 10) || 1) })}
            style={{ width: 56, padding: '6px 8px', border: `1px solid ${COLOR_BORDE}`, borderRadius: 6, fontSize: 13, fontFamily: PP, boxSizing: 'border-box' }}
          />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span title={detalleTecnico(l.estadoOriginal)} style={{ ...estiloBadge(col), cursor: 'help' }}>{etiquetaGrupo(g)}</span>
            <span style={{ fontFamily: 'monospace', fontSize: 12, color: COLOR_OSCURO }}>{l.clave}</span>
          </div>

          {!l.excluida && (
            <div style={{ marginTop: 6 }}>
              {l.esServicio ? (
                <div style={{ fontSize: 12, color: COLOR_GRIS }}>
                  Concepto manual (sin producto).{l.descripcion ? ` ${l.descripcion.slice(0, 80)}` : ''}
                </div>
              ) : prod ? (
                <div style={{ fontSize: 13, color: COLOR_OSCURO }}>
                  <strong style={{ fontFamily: 'monospace' }}>{prod.modelo}</strong>{' '}
                  <span style={{ color: COLOR_GRIS }}>{(prod.titulo || '').slice(0, 60)}</span>
                </div>
              ) : l.candidatos.length > 0 ? (
                <div>
                  <div style={{ fontSize: 12, color: COLORES.dudoso.texto, marginBottom: 4 }}>Elige cual de {l.candidatos.length}:</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 170, overflowY: 'auto' }}>
                    {l.candidatos.map((c, k) => <OpcionProducto key={k} prod={c} onElegir={() => onPatch({ producto: c, buscando: false })} />)}
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: 12, color: COLORES.error.texto }}>
                  No se encontro en el catalogo. Busca y asigna el producto, o marcalo como servicio.
                </div>
              )}

              {l.descripcion && !l.esServicio && (
                <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 3 }}>{l.descripcion.slice(0, 90)}</div>
              )}

              {l.buscando && (
                <BuscadorInline
                  onElegir={(p) => onPatch({ producto: p, esServicio: false, buscando: false })}
                />
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: 14, marginTop: 8, flexWrap: 'wrap' }}>
            {l.excluida ? (
              <button onClick={() => onPatch({ excluida: false })} style={estiloAccion('#185FA5')}>↩ Restaurar</button>
            ) : (
              <>
                {!l.esServicio && (
                  <button onClick={() => onPatch({ buscando: !l.buscando })} style={estiloAccion('#185FA5')}>
                    {l.buscando ? 'Cerrar buscador' : (prod ? '🔁 Cambiar producto' : '🔎 Buscar producto')}
                  </button>
                )}
                <button
                  onClick={() => onPatch({ esServicio: !l.esServicio, producto: l.esServicio ? l.producto : null, buscando: false })}
                  style={estiloAccion(COLOR_GRIS)}
                >{l.esServicio ? '↩ Es producto' : '🛠️ Marcar como servicio'}</button>
                <button onClick={() => onPatch({ excluida: true })} style={estiloAccion('#e05c5c')}>✕ Quitar</button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Buscador inline contra el catalogo de La Tienda (/api/tienda/buscar).
function BuscadorInline({ onElegir }) {
  const [q, setQ] = useState('');
  const [cargando, setCargando] = useState(false);
  const [resultados, setResultados] = useState([]);
  const debRef = useRef(null);

  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    const term = q.trim();
    if (term.length < 2) { setResultados([]); return undefined; }
    debRef.current = setTimeout(async () => {
      setCargando(true);
      try {
        const url = `${API_BASE_CLI}/api/tienda/buscar?fuente=syscom&limit=12&q=${encodeURIComponent(term)}`;
        const r = await fetch(url, { headers: authHeaders() }).then(x => x.json());
        const prods = (r && r.productos) || [];
        setResultados(prods
          .map(p => ({ producto_id: p.referencia_id, modelo: p.modelo, titulo: p.nombre, img_portada: p.imagen_url, marca: p.marca }))
          .filter(p => p.producto_id));
      } catch { setResultados([]); }
      finally { setCargando(false); }
    }, 350);
    return () => { if (debRef.current) clearTimeout(debRef.current); };
  }, [q]);

  return (
    <div style={{ marginTop: 8, border: `1px solid ${COLOR_BORDE}`, borderRadius: 8, padding: 8, background: '#fafbfc' }}>
      <input
        autoFocus type="text" value={q} onChange={(e) => setQ(e.target.value)}
        placeholder="Buscar por modelo o nombre en el catalogo…"
        style={{ width: '100%', padding: '8px 10px', border: `1px solid ${COLOR_BORDE}`, borderRadius: 6, fontSize: 13, fontFamily: PP, boxSizing: 'border-box' }}
      />
      <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 200, overflowY: 'auto' }}>
        {cargando && <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, padding: 4 }}>Buscando…</div>}
        {!cargando && q.trim().length >= 2 && resultados.length === 0 && (
          <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, padding: 4 }}>Sin resultados.</div>
        )}
        {resultados.map((p, k) => <OpcionProducto key={k} prod={p} onElegir={() => onElegir(p)} />)}
      </div>
    </div>
  );
}

// Tarjeta clicable de un producto (candidato o resultado de busqueda).
function OpcionProducto({ prod, onElegir }) {
  return (
    <button
      type="button" onClick={onElegir}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left', width: '100%',
        background: '#fff', border: `1px solid ${COLOR_BORDE}`, borderRadius: 8, padding: 6, cursor: 'pointer', fontFamily: PP,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = '#f6f9fc'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
    >
      <Miniatura src={prod.img_portada} grupo="seguro" />
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#6d28d9', fontFamily: 'monospace' }}>{prod.modelo || '—'}</span>
          {prod.fuente && (
            <span style={{
              fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3,
              padding: '1px 6px', borderRadius: 10,
              background: prod.fuente === 'beep' ? '#e8faf5' : '#eef2fb',
              color: prod.fuente === 'beep' ? '#1D9E75' : COLOR_OSCURO,
            }}>{prod.fuente === 'beep' ? 'Mis Productos' : 'Syscom'}</span>
          )}
        </div>
        <div style={{ fontSize: 11, color: COLOR_GRIS, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {(prod.titulo || '').slice(0, 70)}
        </div>
        {prod.costo_sin_iva != null && Number(prod.costo_sin_iva) > 0 && (
          <div style={{ fontSize: 10, color: COLOR_GRIS_CLARO }}>
            Costo {Number(prod.costo_sin_iva).toLocaleString('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2, maximumFractionDigits: 2 })} (sin IVA)
          </div>
        )}
      </div>
    </button>
  );
}

// Miniatura del producto (misma img_portada que La Tienda). Placeholder si no hay.
function Miniatura({ src, grupo }) {
  const [falla, setFalla] = useState(false);
  const caja = {
    width: 38, height: 38, borderRadius: 6, border: `1px solid ${COLOR_BORDE}`,
    background: '#fff', display: 'block', objectFit: 'contain', flexShrink: 0,
  };
  if (src && !falla) {
    return <img src={src} alt="" loading="lazy" style={caja} onError={() => setFalla(true)} />;
  }
  return (
    <div style={{ ...caja, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f3f4f6', color: COLOR_GRIS_CLARO, fontSize: 16 }}>
      {grupo === 'servicio' ? '🛠️' : '📦'}
    </div>
  );
}

function estiloBadge(col) {
  return {
    display: 'inline-block', padding: '2px 8px', borderRadius: 6,
    background: col.bg, border: `1px solid ${col.borde}`, color: col.texto, fontSize: 11, fontWeight: 600,
  };
}
function estiloAccion(color) {
  return { background: 'none', border: 'none', color, fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0, fontFamily: PP };
}
